import React from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Radio,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import HourglassTopIcon from '@mui/icons-material/HourglassTop'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { de } from 'date-fns/locale/de'
import { enUS } from 'date-fns/locale/en-US'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import NumberField from '../NumberField'
import { IProject, IProjectAndActivity } from '../../interface/IProjects'
import { PMJournalHeaderType, PMJournalLineType, AI_JOURNAL, YE_JOURNAL, EMPTY_GUID } from './PMJournal.types'

interface Props {
    currentHeader: PMJournalHeaderType | null
    isNew: boolean
    journalType: string
    setJournalType: (v: string) => void
    selectedProjectId: string
    setSelectedProjectId: (v: string) => void
    journalDate: Date | null
    setJournalDate: (v: Date | null) => void
    lines: PMJournalLineType[]
    setLines: React.Dispatch<React.SetStateAction<PMJournalLineType[]>>
    saving: boolean
    releasing: boolean
    deleting: boolean
    reopening: boolean
    isBusy: boolean
    errorMsg: string | null
    permitFrom: Date | null
    permitTo: Date | null
    isWithinYeWindow: boolean
    yeWindowFrom: Date | null
    yeWindowTo: Date | null
    saveJournal: () => void
    releaseJournal: () => void
    reopenJournal: () => void
    deleteJournal: () => void
    addLine: () => void
    updateLine: (idx: number, field: keyof PMJournalLineType, value: any) => void
    removeLine: (idx: number) => void
    getFilteredActivities: (projectId: string) => IProjectAndActivity[]
    getFilteredProjects: () => IProject[]
    projectName: (id: string) => string
    setViewMode: (v: 'list' | 'editor') => void
    projects: IProject[]
}

const PMJournalEditor = ({
    currentHeader, isNew,
    journalType, setJournalType,
    selectedProjectId, setSelectedProjectId,
    journalDate, setJournalDate,
    lines, setLines,
    saving, releasing, deleting, reopening, isBusy,
    errorMsg, permitFrom, permitTo, isWithinYeWindow, yeWindowFrom, yeWindowTo,
    saveJournal, releaseJournal, reopenJournal, deleteJournal,
    addLine, updateLine, removeLine,
    getFilteredActivities, getFilteredProjects, setViewMode,
}: Props) => {
    const { t, i18n } = useTranslation()
    const dateLocale = i18n.language === 'de' ? de : enUS

    const posted = currentHeader?.posted ?? false
    const hasId = !!currentHeader?.projJournalTableEntityId
    const activities = getFilteredActivities(selectedProjectId)
    const hasProject = journalType === AI_JOURNAL && !!selectedProjectId
    const showNoAiActivities = hasProject && activities.length === 0
    const showNoAiResource = hasProject && activities.length > 0 && activities.some((a) => !a.taskResources?.length)
    const showMultipleAiResources = hasProject && activities.length > 0 && activities.some((a) => (a.taskResources?.length ?? 0) > 1)
    const showNoYeWarning = journalType === YE_JOURNAL && !!selectedProjectId && activities.length === 0
    const totalHours = lines.filter(l => !l.delete).reduce((sum, l) => sum + (l.hours ?? 0), 0)
    const yeSelectable = isNew && isWithinYeWindow
    const journalDateOnly = journalDate ? new Date(journalDate.getFullYear(), journalDate.getMonth(), journalDate.getDate()) : null
    const isDateOutsidePermit = journalDateOnly != null && (
        (permitFrom != null && journalDateOnly < new Date(permitFrom.getFullYear(), permitFrom.getMonth(), permitFrom.getDate())) ||
        (permitTo != null && journalDateOnly > new Date(permitTo.getFullYear(), permitTo.getMonth(), permitTo.getDate()))
    )
    const formatPermitDate = (d: Date | null) =>
        d ? d.toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''
    const releaseDisabledTooltip = isDateOutsidePermit
        ? t('pmJournal.releaseDisabledOutsidePeriod', { from: formatPermitDate(permitFrom), to: formatPermitDate(permitTo) })
        : ''
    const formatWindowDate = (d: Date | null) =>
        d ? d.toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US', { day: '2-digit', month: '2-digit' }) : ''
    const yeWindowTooltip = isNew && !isWithinYeWindow
        ? t('pmJournal.yeWindowClosed', { from: formatWindowDate(yeWindowFrom), to: formatWindowDate(yeWindowTo) })
        : ''

    return (
        <>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h5">
                        {t('pmJournal.editorTitle')}{currentHeader?.batchnumber ? ` – ${currentHeader.batchnumber}` : ''}
                    </Typography>
                    {currentHeader?.projJournalTableEntityId && (
                        posted
                            ? <Chip label={t('pmJournal.posted')} color="success" size="small" />
                            : (currentHeader.projJournalTableEntityId !== EMPTY_GUID)
                                ? <Chip label={t('pmJournal.saved')} color="success" size="small" variant="outlined" />
                                : <Chip label={t('pmJournal.unposted')} color="default" size="small" />
                    )}
                </Box>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>

                    {/* Block: Typ */}
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>{t('pmJournal.stepTyp')}</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>

                            {/* AI Hours card */}
                            <Box
                                onClick={() => isNew && setJournalType(AI_JOURNAL)}
                                sx={{
                                    flex: 1,
                                    border: '2px solid #8371F0',
                                    borderRadius: 2,
                                    p: 2,
                                    cursor: isNew ? 'pointer' : 'default',
                                    opacity: isNew ? 1 : 0.6,
                                    bgcolor: journalType === AI_JOURNAL ? '#8371F0' : 'transparent',
                                    color: journalType === AI_JOURNAL ? '#fff' : 'inherit',
                                    transition: 'background-color 0.2s, color 0.2s, transform 0.15s, box-shadow 0.15s',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1.5,
                                    ...(isNew && journalType !== AI_JOURNAL && {
                                        '&:hover': { transform: 'translateY(-3px)', boxShadow: 4 },
                                    }),
                                }}
                            >
                                <Radio
                                    checked={journalType === AI_JOURNAL}
                                    onChange={() => isNew && setJournalType(AI_JOURNAL)}
                                    disabled={!isNew}
                                    size="small"
                                    sx={{
                                        p: 0,
                                        color: journalType === AI_JOURNAL ? '#fff' : 'inherit',
                                        '&.Mui-checked': { color: journalType === AI_JOURNAL ? '#fff' : '#8371F0' },
                                    }}
                                />
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                                        <AutoAwesomeIcon fontSize="small" />
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                                            {t('pmJournal.aiFteLabel')}
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" sx={{ lineHeight: 1.4, display: 'block' }}>
                                        {t('pmJournal.typAiDescription')}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* YE Hours card */}
                            <Tooltip
                                title={yeWindowTooltip}
                                placement="top"
                                arrow
                            >
                            <Box
                                onClick={() => yeSelectable && setJournalType(YE_JOURNAL)}
                                sx={{
                                    flex: 1,
                                    border: '2px solid #198585',
                                    borderRadius: 2,
                                    p: 2,
                                    cursor: yeSelectable ? 'pointer' : 'default',
                                    opacity: yeSelectable ? 1 : 0.4,
                                    bgcolor: journalType === YE_JOURNAL ? '#198585' : 'transparent',
                                    color: journalType === YE_JOURNAL ? '#fff' : 'inherit',
                                    transition: 'background-color 0.2s, color 0.2s, transform 0.15s, box-shadow 0.15s',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1.5,
                                    ...(yeSelectable && journalType !== YE_JOURNAL && {
                                        '&:hover': { transform: 'translateY(-3px)', boxShadow: 4 },
                                    }),
                                }}
                            >
                                <Radio
                                    checked={journalType === YE_JOURNAL}
                                    onChange={() => yeSelectable && setJournalType(YE_JOURNAL)}
                                    disabled={!yeSelectable}
                                    size="small"
                                    sx={{
                                        p: 0,
                                        color: journalType === YE_JOURNAL ? '#fff' : 'inherit',
                                        '&.Mui-checked': { color: journalType === YE_JOURNAL ? '#fff' : '#198585' },
                                    }}
                                />
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                                        <HourglassTopIcon fontSize="small" />
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                                            {t('pmJournal.yeLabel')}
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" sx={{ lineHeight: 1.4, display: 'block' }}>
                                        {t('pmJournal.typYeDescription')}
                                    </Typography>
                                </Box>
                            </Box>
                            </Tooltip>

                        </Box>
                    </Paper>

                    {/* Block: Kopf */}
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>{t('pmJournal.stepKopf')}</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'flex-start' }}>
                            <TextField
                                select
                                label={t('pmJournal.project')}
                                value={selectedProjectId}
                                onChange={(e) => setSelectedProjectId(e.target.value)}
                                size="small"
                                sx={{ flex: 1 }}
                                disabled={!!currentHeader?.posted || !!currentHeader?.projJournalTableEntityId}
                            >
                                {getFilteredProjects().map((p) => (
                                    <MenuItem key={p.mserp_projectid} value={p.mserp_projectid ?? ''}>
                                        {p.mserp_projectid} – {p.mserp_projectname}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
                                <DatePicker
                                    label={t('pmJournal.buchungsdatum')}
                                    value={journalDate}
                                    onChange={(v) => {
                                        setJournalDate(v)
                                        if (v) {
                                            const formatted = format(v, "yyyy-MM-dd'T'HH:mm:ss")
                                            setLines(prev => prev.map(l => ({ ...l, projectDate: formatted })))
                                        }
                                    }}
                                    disabled={!!currentHeader?.posted}
                                    minDate={permitFrom ?? undefined}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            sx: { flex: 1 },
                                            helperText: t('pmJournal.buchungsdatumHint'),
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>
                    </Paper>

                    {/* Block: Zeilen */}
                    <Tooltip title={!selectedProjectId ? t('pmJournal.selectProjectFirst') : ''} placement="top" arrow>
                        <Box sx={{ display: 'block', ...(!selectedProjectId && { cursor: 'not-allowed' }) }}>
                            <Paper variant="outlined" sx={{ p: 2, ...(!selectedProjectId && { opacity: 0.5, pointerEvents: 'none' }) }}>
                        <Typography variant="subtitle2" gutterBottom>{t('pmJournal.stepZeilen')}</Typography>
                        {showNoAiActivities && (
                            <Box
                                sx={{
                                    mb: 2,
                                    p: 1.5,
                                    border: '1px solid',
                                    borderColor: 'warning.main',
                                    borderRadius: 1,
                                    backgroundColor: 'warning.light',
                                    color: 'warning.contrastText',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1,
                                }}
                            >
                                <WarningAmberIcon fontSize="small" sx={{ mt: '2px', flexShrink: 0 }} />
                                <Typography variant="body2">{t('pmJournal.noAiActivities')}</Typography>
                            </Box>
                        )}
                        {showNoAiResource && (
                            <Box
                                sx={{
                                    mb: 2,
                                    p: 1.5,
                                    border: '1px solid',
                                    borderColor: 'warning.main',
                                    borderRadius: 1,
                                    backgroundColor: 'warning.light',
                                    color: 'warning.contrastText',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1,
                                }}
                            >
                                <WarningAmberIcon fontSize="small" sx={{ mt: '2px', flexShrink: 0 }} />
                                <Typography variant="body2">{t('pmJournal.noAiResource')}</Typography>
                            </Box>
                        )}
                        {showMultipleAiResources && (
                            <Box
                                sx={{
                                    mb: 2,
                                    p: 1.5,
                                    border: '1px solid',
                                    borderColor: 'warning.main',
                                    borderRadius: 1,
                                    backgroundColor: 'warning.light',
                                    color: 'warning.contrastText',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1,
                                }}
                            >
                                <WarningAmberIcon fontSize="small" sx={{ mt: '2px', flexShrink: 0 }} />
                                <Typography variant="body2">{t('pmJournal.multipleAiResources')}</Typography>
                            </Box>
                        )}
                        {showNoYeWarning && (
                            <Box
                                sx={{
                                    mb: 2,
                                    p: 1.5,
                                    border: '1px solid',
                                    borderColor: 'warning.main',
                                    borderRadius: 1,
                                    backgroundColor: 'warning.light',
                                    color: 'warning.contrastText',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1,
                                }}
                            >
                                <WarningAmberIcon fontSize="small" sx={{ mt: '2px', flexShrink: 0 }} />
                                <Typography variant="body2">{t('pmJournal.noYeActivities')}</Typography>
                            </Box>
                        )}
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: 56 }}>
                                            {!posted && (
                                                <Tooltip title={!selectedProjectId ? t('pmJournal.selectProjectFirst') : t('pmJournal.addLine')}>
                                                    <span>
                                                        <IconButton size="small" onClick={addLine} disabled={!selectedProjectId}>
                                                            <AddCircleIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell>{t('pmJournal.activity')}</TableCell>
                                        <TableCell sx={{ width: 120 }}>{t('pmJournal.hours')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {lines.filter((l) => !l.delete).length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} sx={{ textAlign: 'center', color: 'text.secondary', py: 3 }}>
                                                {t('pmJournal.noLines')}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {lines.map((line, idx) =>
                                        line.delete ? null : (
                                            <TableRow key={idx}>
                                                <TableCell>
                                                    {!posted && (
                                                        <Tooltip title={t('pmJournal.deleteLine')}>
                                                            <IconButton size="small" onClick={() => removeLine(idx)}>
                                                                <DeleteOutlinedIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        select
                                                        value={line.activityNumber}
                                                        onChange={(e) => updateLine(idx, 'activityNumber', e.target.value)}
                                                        size="small"
                                                        fullWidth
                                                        disabled={!!currentHeader?.posted || !selectedProjectId}
                                                        error={line.activityNumber === ''}
                                                    >
                                                        {activities.map((a) => (
                                                            <MenuItem key={a.mserp_activitynumber} value={a.mserp_activitynumber ?? ''}>
                                                                {a.mserp_category ? `${a.mserp_description} (${a.mserp_category})` : a.mserp_description}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                </TableCell>
                                                <TableCell>
                                                    <NumberField
                                                        value={Math.floor(Math.round((line.hours ?? 0) * 10000) / 100) / 100}
                                                        size="small"
                                                        disabled={!!currentHeader?.posted || !selectedProjectId}
                                                        sx={{ width: '120px' }}
                                                        onValueChange={(value: number | null) => {
                                                            if (value !== null && value !== undefined)
                                                                updateLine(idx, 'hours', Math.floor(Number((value * 100).toFixed(1))) / 100)
                                                        }}
                                                        format={{
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                            useGrouping: false,
                                                        }}
                                                        step={0.25}
                                                        min={0}
                                                        endAdornment={<InputAdornment position="start">h</InputAdornment>}
                                                        error={line.hours <= 0 || line.hours % 0.25 !== 0}
                                                        helperText={line.hours > 0 && line.hours % 0.25 !== 0 ? t('zdelines.journalinvalidhours') : ''}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {lines.filter(l => !l.delete).length > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, pr: 0.5 }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {t('pmJournal.totalHours')}:&nbsp;
                                    <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                        {totalHours.toLocaleString(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h
                                    </Box>
                                </Typography>
                            </Box>
                        )}
                            </Paper>
                        </Box>
                    </Tooltip>


                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 1 }}>
                    {errorMsg && (
                        <Typography color="error" variant="body2" sx={{ width: '100%' }}>
                            {errorMsg}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <Button startIcon={<ArrowBackIcon />} onClick={() => setViewMode('list')} size="small">
                            {t('pmJournal.backToList')}
                        </Button>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {hasId && !posted && (
                                <Button variant="outlined" color="error" onClick={deleteJournal} disabled={isBusy} size="small">
                                    {deleting ? <CircularProgress size={18} /> : t('pmJournal.delete')}
                                </Button>
                            )}
                            {!posted && (
                                <Button variant="outlined" color="primary" onClick={saveJournal} disabled={isBusy} size="small">
                                    {saving ? <CircularProgress size={18} /> : t('pmJournal.save')}
                                </Button>
                            )}
                            {!posted && (
                                <Tooltip title={releaseDisabledTooltip} arrow>
                                    <span>
                                        <Button variant="contained" color="primary" onClick={releaseJournal} disabled={isBusy || isDateOutsidePermit} size="small">
                                            {releasing ? <CircularProgress size={18} /> : t('pmJournal.release')}
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}
                            {hasId && posted && (
                                <Button variant="contained" color="warning" onClick={reopenJournal} disabled={isBusy} size="small">
                                    {reopening ? <CircularProgress size={18} /> : t('pmJournal.reopen')}
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>
            </DialogActions>
        </>
    )
}

export default PMJournalEditor
